import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const suboxoneQuestions = [
  {
    field: 'suboxone_telemedicineConsent',
    label: `I understand this is a telemedicine visit and consent to receive medical care via telehealth, including evaluation and treatment for opioid use disorder with buprenorphine-containing medications as clinically appropriate. I understand federal telemedicine rules currently allow prescribing of controlled medications via telehealth under temporary flexibilities that run through December 31, 2025; if DEA's 2025 telemedicine rule becomes effective afterward, audio-only initiations of buprenorphine may be limited to an initial 6-month supply unless I have an in-person exam. I acknowledge my clinician will review the state Prescription Drug Monitoring Program (PDMP) as allowed/required. 42 CFR Part 2 consent: I authorize the program to use and disclose my substance use disorder information for treatment, payment, and health care operations as described; I understand I may revoke this consent and that redisclosure is restricted by law.`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value: 'I have read and understand the above information and I wish to continue',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'suboxone_emergencySymptoms',
    label: 'Do you currently have any of the following symptoms:',
    educationalSlide: `➢ severe chest pain, 
➢ trouble breathing, confusion, 
➢ uncontrolled vomiting, 
➢ severe dehydration, 
➢ fever >101°F (38.3°C), 
➢ you feel unsafe where you are`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'suboxone_emergencyReferral',
    label:
      'Based on your symptoms, you may be experiencing a medical emergency. Please contact emergency services by calling 911 immediately.',
    type: 'info',
    blockQuestionFlow: true,
    isLast: true,
    answer: {},
    extras: { sendToBeluga: false }
  },
  {
    field: 'suboxone_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    extras: { sendToBeluga: false },
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'suboxone_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'suboxone_pregnancyConsent',
    label: 'Consent (pregnancy)',
    type: 'select',
    educationalSlide: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy or while breastfeeding. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'suboxone_heightFeet',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_heightFeet',
      placeholder: 'e.g. 5 ft 8 in'
    }
  },
  {
    field: 'suboxone_weightPounds',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'number',
      name: 'suboxone_weightPounds'
    }
  },
  {
    field: 'suboxone_primaryGoal',
    label: 'What is your primary treatment goal with suboxone?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Stop illicit opioid use', key: 'stop_illicit_use' },
        { value: 'Reduce cravings/withdrawal', key: 'reduce_cravings' },
        { value: 'Lower overdose risk', key: 'lower_overdose_risk' },
        {
          value: 'Improve work/school/family functioning',
          key: 'improve_functioning'
        },
        { value: 'Transition from methadone', key: 'transition_methadone' },
        {
          value: 'Manage chronic pain while in OUD treatment',
          key: 'manage_chronic_pain'
        },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'suboxone_primaryGoalOther',
    label: 'Tell us a bit more about your other goal',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_primaryGoalOther',
      rows: 4
    }
  },
  {
    field: 'suboxone_opioidDuration',
    label:
      'How long have you taken opioids, either prescribed by a medical provider or recreationally?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: '< 6 months', key: 'less_than_6_months' },
        { value: '6-12 months', key: '6_to_12_months' },
        { value: '1-3 years', key: '1_to_3_years' },
        { value: '> 3 years', key: 'more_than_3_years' }
      ]
    }
  },
  {
    field: 'suboxone_primaryOpioids',
    label:
      'Which are the primary opioids that you have used in the past 30 days? (select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Fentanyl', key: 'fentanyl' },
        { value: 'Heroin', key: 'heroin' },
        { value: 'Oxycodone', key: 'oxycodone' },
        { value: 'Hydrocodone', key: 'hydrocodone' },
        { value: 'Morphine', key: 'morphine' },
        { value: 'Methadone', key: 'methadone' },
        { value: 'Tramadol', key: 'tramadol' },
        { value: 'Hydromorphone', key: 'hydromorphone' },
        { value: 'Codeine', key: 'codeine' },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'suboxone_otherOpioids',
    label: 'Tell us about the other opioid(s) that you take',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_otherOpioids',
      rows: 4
    }
  },
  {
    field: 'suboxone_opioidRoutes',
    label:
      'What are the typical routes that you have used within the past 30 days? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Oral', key: 'oral' },
        { value: 'Snorting', key: 'snorting' },
        { value: 'Smoking', key: 'smoking' },
        { value: 'Injection (into vein)', key: 'injection_vein' },
        { value: 'Injection (under the skin)', key: 'injection_skin' },
        { value: 'Transdermal patch', key: 'transdermal_patch' }
      ]
    }
  },
  {
    field: 'suboxone_lastOpiateUse',
    label:
      'When was the last time that you used an opiate? Please include the month, day, and year.',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_lastOpiateUse',
    }
  },
  {
    field: 'suboxone_lastOpioidDetails',
    label:
      'Please provide the name of the last opioid that you used and the approximate milligram (mg) intake',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_lastOpioidDetails',
      rows: 3
    }
  },
  {
    field: 'suboxone_withdrawalSymptoms',
    label: 'Are you currently experiencing withdrawal symptoms?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'suboxone_cowsHeartRate',
    label:
      'After sitting still for one minute, how fast is your heartbeat?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: '80 or below beats per minute', key: 'score_0' },
        { value: '81-100 beats per minute', key: 'score_1' },
        { value: '101-120 beats per minute', key: 'score_2' },
        { value: '>120 beats per minute', key: 'score_4' }
      ]
    }
  },
  {
    field: 'suboxone_cowsSweating',
    label:
      'In the past 30 minutes, not due to room temperature or exercise, have you noticed sweating or chills?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No chills or flushing', key: 'score_0' },
        { value: 'I have chills or flushing', key: 'score_1' },
        { value: 'I have some moistness on my face', key: 'score_2' },
        { value: 'I have beads of sweat on my brow or face', key: 'score_3' },
        {
          value: 'I have sweat streaming off my face',
          key: 'score_4'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsRestlessness',
    label: 'Are you able to sit still during this assessment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: "I'm able to sit still", key: 'score_0' },
        {
          value: 'I have some difficulty sitting still, fidgety',
          key: 'score_1'
        },
        {
          value:
            "I'm frequent shifting/extraneous movements of legs/arms",
          key: 'score_3'
        },
        {
          value: "I'm unable to sit still for more than a few seconds",
          key: 'score_5'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsPupilSize',
    label:
      'Look at your pupils in normal light, please select the most appropriate description',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'My pupils pinned or normal for room light',
          key: 'score_0'
        },
        { value: 'They are larger than normal', key: 'score_1' },
        { value: 'They are moderately dilated', key: 'score_2' },
        {
          value:
            'They are so dilated that only the rim of iris (colored section) is visible',
          key: 'score_5'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsBoneMuscleAches',
    label:
      'Do you have any bone or muscle aches right now (not from an old injury)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Not present', key: 'score_0' },
        { value: 'I have mild diffuse discomfort', key: 'score_1' },
        {
          value: 'I have severe diffuse aching of joints/muscles',
          key: 'score_2'
        },
        { value: "I'm in constant severe diffuse aching", key: 'score_4' }
      ]
    }
  },
  {
    field: 'suboxone_cowsRunnyNose',
    label:
      'Have you had a runny nose or watery eyes (not from allergies/cold)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Not present', key: 'score_0' },
        {
          value: 'I have occasional nasal stuffiness/tearing',
          key: 'score_1'
        },
        { value: 'I often have nasal discharge/tears', key: 'score_2' },
        {
          value: 'My nose constantly running or tears streaming',
          key: 'score_4'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsGiSymptoms',
    label: 'How is your stomach feeling right now?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No GI symptoms', key: 'score_0' },
        { value: 'I have stomach cramps', key: 'score_1' },
        { value: 'I have nausea or loose stool', key: 'score_2' },
        { value: "I'm vomiting or having diarrhea", key: 'score_3' },
        {
          value: "I'm having multiple episodes of diarrhea or vomiting",
          key: 'score_5'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsTremor',
    label: 'Stretch your hands out in front of you. Do they shake?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No tremor', key: 'score_0' },
        { value: 'My tremor can be felt, not seen', key: 'score_1' },
        { value: 'I have a slight tremor observable', key: 'score_2' },
        {
          value: 'I have a very obvious tremor or muscle twitching',
          key: 'score_4'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsYawning',
    label: 'Have you yawned during this assessment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No yawning', key: 'score_0' },
        { value: 'Yawning once or twice', key: 'score_1' },
        { value: 'Yawning three or more times', key: 'score_2' },
        { value: 'Yawning several times per minute', key: 'score_4' }
      ]
    }
  },
  {
    field: 'suboxone_cowsAnxiety',
    label: 'How anxious or irritable do you feel right now?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'None', key: 'score_0' },
        {
          value: "I'm feeling mildly anxious/irritable",
          key: 'score_1'
        },
        {
          value: "I'm feeling moderately anxious/irritable",
          key: 'score_2'
        },
        {
          value: "I'm feeling extremely anxious/irritable",
          key: 'score_4'
        }
      ]
    }
  },
  {
    field: 'suboxone_cowsGoosebumps',
    label: 'Look at your skin. Do you notice goosebumps?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Skin smooth', key: 'score_0' },
        {
          value: 'Goosebumps on my skin can be felt or observed',
          key: 'score_3'
        },
        { value: 'I have prominent goosebumps', key: 'score_5' }
      ]
    }
  },
  {
    field: 'suboxone_cravingSeverity',
    label: 'Rank your current craving severity (0-5)',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: '0 - I do not have any cravings', key: 'none_0' },
        { value: '1 - Very mild cravings', key: 'very_mild_1' },
        { value: '2 - Mild cravings', key: 'mild_2' },
        { value: '3 - Moderate cravings', key: 'moderate_3' },
        { value: '4 - Strong cravings', key: 'strong_4' },
        { value: '5 - Severe cravings', key: 'severe_5' }
      ]
    }
  },
  {
    field: 'suboxone_priorOverdose',
    label:
      'Have you had a prior overdose requiring naloxone or emergency care?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Never', key: 'never' },
        { value: 'Once', key: 'once' },
        { value: '2-3 times', key: '2_to_3_times' },
        { value: '4+ times', key: '4_plus_times' }
      ]
    }
  },
  {
    field: 'suboxone_priorTreatment',
    label: 'Have you received prior treatment for opioid use disorder?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'Yes I have had treatment before which was WITHIN the last 12 months',
          key: 'within_12_months'
        },
        {
          value:
            'Yes I have had treatment before and it has been OVER 12 months ago',
          key: 'over_12_months'
        },
        { value: 'No, this will be my first time', key: 'no_first_time' }
      ]
    }
  },
  {
    field: 'suboxone_priorTreatments',
    label: 'What prior treatments have you tried? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Buprenorphine/naloxone', key: 'buprenorphine_naloxone' },
        { value: 'Buprenorphine mono-product', key: 'buprenorphine_mono' },
        {
          value: 'Extended-release buprenorphine injection (Sublocade/Brixadi)',
          key: 'extended_release_buprenorphine'
        },
        { value: 'Methadone via OTP', key: 'methadone_otp' },
        { value: 'Naltrexone (oral/XR)', key: 'naltrexone' },
        { value: 'Inpatient/residential program', key: 'inpatient_residential' },
        { value: 'Intensive outpatient', key: 'intensive_outpatient' },
        { value: 'Mutual-help (NA/SMART)', key: 'mutual_help' }
      ]
    }
  },
  {
    field: 'suboxone_buprenorphineStopReasons',
    label:
      'If you stopped a prior buprenorphine treatment, what were the main reasons? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Side effects', key: 'side_effects' },
        {
          value: "Couldn't access appointments/pharmacy",
          key: 'access_issues'
        },
        { value: 'Cost/insurance', key: 'cost_insurance' },
        { value: 'Stigma/privacy', key: 'stigma_privacy' },
        { value: 'Precipitated withdrawal', key: 'precipitated_withdrawal' },
        {
          value: "Didn't control cravings/continued use",
          key: 'didnt_control_cravings'
        },
        {
          value: 'Preferred a different treatment option',
          key: 'preferred_different_treatment'
        },
        { value: 'Legal/court issues', key: 'legal_court_issues' },
        { value: 'Other reasons', key: 'other_reasons' }
      ]
    }
  },
  {
    field: 'suboxone_buprenorphineStopReasonsOther',
    label: 'Tell us more about the other reason you stopped this medication',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_buprenorphineStopReasonsOther',
      rows: 4
    }
  },
  {
    field: 'suboxone_buprenorphineSideEffects',
    label:
      'Tell us a bit more about the side effects that you experienced (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Precipitated withdrawal', key: 'precipitated_withdrawal' },
        { value: 'Severe sedation', key: 'severe_sedation' },
        { value: 'Severe constipation', key: 'severe_constipation' },
        { value: 'Itching/rash/oral irritation', key: 'itching_rash' },
        { value: 'LFT elevation', key: 'lft_elevation' },
        { value: 'Diversion concerns', key: 'diversion_concerns' },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'suboxone_buprenorphineSideEffectsOther',
    label: 'Tell us more about your other side effect',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_buprenorphineSideEffectsOther',
      rows: 4
    }
  },
  {
    field: 'suboxone_benzodiazepineUse',
    label:
      'Are you currently using benzodiazepines (e.g., alprazolam, clonazepam), hypnotic sleep aides (zolpidem/Ambien), barbiturates, or heavy alcohol use?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes, daily', key: 'daily' },
        { value: 'Yes, occasionally', key: 'occasionally' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'suboxone_benzodiazepineConsent',
    label: 'Benzodiazepine and alcohol interaction consent',
    type: 'select',
    educationalSlide: `Taking benzodiazepines (such as Xanax, Valium, Ativan, or Klonopin), hypnotic sleep aides, and heavy alcohol use together with Suboxone (buprenorphine/naloxone) can significantly increase the risk of serious side effects. Both medications slow down brain activity and can depress breathing. When used together, the effects add up, which may lead to extreme drowsiness, confusion, low blood pressure, poor coordination, slowed or difficult breathing, coma, or even death. Because of these dangers, these substances should not be taken with this medication.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value: 'I understand and agree to withhold use of those medications',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'suboxone_medicalConditions',
    label: 'Do you have any of the following conditions? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Moderate or severe liver disease (hepatitis, cirrhosis)',
          key: 'liver_disease'
        },
        {
          value: 'Prior elevated liver enzymes while on buprenorphine',
          key: 'elevated_liver_enzymes'
        },
        {
          value: 'Severe chronic lung disease (COPD/emphysema)',
          key: 'severe_lung_disease'
        },
        { value: 'Severe sleep apnea', key: 'severe_sleep_apnea' },
        {
          value: 'Severe chronic kidney disease (on dialysis or eGFR <30)',
          key: 'severe_kidney_disease'
        },
        {
          value:
            'Allergic reaction or adverse reaction to suboxone (buprenorphine/naloxone)',
          key: 'suboxone_reaction'
        },
        {
          value: 'I do NOT have any of the following conditions',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'suboxone_medications',
    label: 'Do you take any of the following medications?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Ketoconazole', key: 'ketoconazole' },
        { value: 'Clarithromycin', key: 'clarithromycin' },
        { value: 'Ritonavir', key: 'ritonavir' },
        { value: 'Rifampin', key: 'rifampin' },
        { value: 'Carbamazepine', key: 'carbamazepine' },
        { value: 'Phenytoin', key: 'phenytoin' },
        { value: 'Gabapentin', key: 'gabapentin' },
        { value: 'Pregabalin', key: 'pregabalin' },
        { value: 'None', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'suboxone_naloxonePrescription',
    label:
      'Do you currently have an unexpired prescription for Naloxone at home (Narcan or equivalent)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes, I already have it at my home', key: 'already_have' },
        { value: 'I need a prescription', key: 'need_prescription' },
        { value: 'I do not wish to have this', key: 'do_not_wish' }
      ]
    }
  },
  {
    field: 'suboxone_precipitatedWithdrawalConsent',
    label: `Starting Suboxone (buprenorphine/naloxone) too soon after your last opioid use can cause precipitated withdrawal. This is a sudden and often severe worsening of withdrawal symptoms. To reduce this risk, it is important to wait until you are in at least mild to moderate withdrawal before taking your first dose. Do you understand that Suboxone may cause precipitated withdrawal if started too soon after opioid use.`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'No, I do not understand and would like more information before proceeding',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value: 'Yes, I understand and consent to proceed with treatment',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'suboxone_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxone_additionalQuestions',
      rows: 4
    }
  },
  {
    field: 'suboxone_consentTruthfulness',
    label: 'Consent (Truthfulness)',
    type: 'select',
    educationalSlide: `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'suboxone_consentOUD',
    label: 'Consent (Opioid Use Disorder with Suboxone)',
    type: 'select',
    educationalSlide: `You are requesting treatment with Suboxone (a combination of buprenorphine and naloxone) as part of your treatment plan for opioid use disorder. Suboxone works by partially stimulating the opioid receptors in your brain, which reduces cravings and withdrawal symptoms, while the naloxone component discourages misuse. This medication is FDA-approved for the treatment of opioid dependence and is part of evidence-based medication-assisted treatment (MAT).

Potential Benefits:
➢ Reduction of opioid cravings and withdrawal symptoms
➢ Lower risk of opioid relapse
➢ Decreased risk of overdose compared to illicit opioid use
➢ Improved ability to function in daily life, work, school, and relationships
➢ Opportunity to stabilize and engage in counseling and recovery services

Potential Side Effects:
While Suboxone is generally safe and effective when used as prescribed, it can cause side effects. Some may require urgent care or hospitalization. Serious side effects are rare but can include respiratory depression, coma, or death, especially if combined with sedatives, benzodiazepines, or alcohol.

Common Side Effects:
➢ Headache
➢ Nausea or vomiting
➢ Constipation
➢ Sweating
➢ Insomnia or sleep disturbance
➢ Mouth or tongue irritation (sublingual/film products)

Serious Side Effects:
➢ Respiratory depression (slow or difficult breathing, especially with other sedatives)
➢ Precipitated withdrawal if started too soon after opioid use
➢ Severe allergic reaction (rash, swelling, difficulty breathing)
➢ Liver problems (yellowing of eyes/skin, dark urine, severe fatigue)
➢ Severe sedation or dizziness
➢ Injection site reactions (with extended-release formulations)

Risks and Considerations:
➢ Drug Interactions: Combining Suboxone with benzodiazepines, sedatives, or alcohol increases the risk of overdose and death.
➢ Precipitated Withdrawal: If Suboxone is started too soon after using opioids, you may experience sudden, severe withdrawal.
➢ Liver Function: Suboxone is metabolized in the liver. Patients with liver disease may require closer monitoring.
➢ Dependence: Suboxone itself is an opioid and may cause physical dependence; stopping it suddenly can lead to withdrawal.
➢ Diversion/Misuse: Misuse of Suboxone by injection or sharing with others is unsafe and illegal. Always store your medication in a secure location.

Monitoring and Follow-Up:
You will require regular follow-up visits to monitor your response to Suboxone and assess for side effects. Urine drug screens and Prescription Drug Monitoring Program (PDMP) checks may be performed to support your care and ensure safety. You should keep naloxone (Narcan) at home and inform your family or close contacts how to use it in case of an overdose. Counseling, peer support, and recovery services are strongly recommended as part of your treatment plan.

Consent:
I acknowledge the potential benefits, risks, and side effects of Suboxone treatment for opioid use disorder. I understand the importance of taking the medication exactly as prescribed, storing it securely, and attending follow-up visits. I consent to the use of Suboxone as part of my treatment plan for opioid use disorder.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information and I wish to continue',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    class: 'ineligible',
    field: 'suboxone_didNotConsent',
    label: 'We need your consent to continue',
    subLabel:
      'You did not agree to the terms shown on the previous step. Without that consent, we cannot move forward with your visit or prescribe treatment.',
    extras: { sendToBeluga: false },
    answer: {},
    type: 'info',
    isLast: true,
    blockQuestionFlow: true,
  },
  {
    class: 'ineligible',
    field: 'suboxone_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    isLast: true,
    reject: true,
    answer: {},
    extras: { sendToBeluga: false }
  },
  {
    field: 'suboxone_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    field: 'suboxoneFollowup_healthChanges',
    label:
      'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxoneFollowup_healthChangesDetails',
      rows: 4
    }
  },
  {
    field: 'suboxoneFollowup_medicationDoing',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_medicationExperience',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxoneFollowup_medicationExperience',
      rows: 4
    }
  },
  {
    field: 'suboxoneFollowup_medicationAdherence',
    label: 'How often have you taken your medication since your last visit?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Every day as prescribed', key: 'every_day' },
        { value: 'Missed 1-2 doses', key: 'missed_1_2' },
        { value: 'Missed 3 or more doses', key: 'missed_3_plus' },
        { value: 'I stopped taking the medication', key: 'stopped' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_doseCravingControl',
    label: 'Do you feel your current dose is controlling your cravings?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Fully controlled', key: 'fully_controlled' },
        {
          value: 'Mostly controlled, mild symptoms',
          key: 'mostly_controlled'
        },
        { value: 'Partially controlled', key: 'partially_controlled' },
        { value: 'Not controlled', key: 'not_controlled' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_opioidUseSinceLastVisit',
    label:
      'Have you used opioids (including fentanyl, heroin, or prescription opioids) since your last visit?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes, once or twice', key: 'once_or_twice' },
        { value: 'Yes, weekly', key: 'weekly' },
        { value: 'Yes, several times per week', key: 'several_times_week' },
        { value: 'Yes, daily or near daily', key: 'daily_near_daily' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_opioidUseDetails',
    label: 'Please provide the date and type of opioid used',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxoneFollowup_opioidUseDetails',
      rows: 4
    }
  },
  {
    field: 'suboxoneFollowup_sideEffects',
    label:
      'Have you experienced any side effects since your last visit? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'None side effects', key: 'none', uncheckOthers: true },
        { value: 'Nausea/vomiting', key: 'nausea_vomiting' },
        { value: 'Constipation', key: 'constipation' },
        { value: 'Headache', key: 'headache' },
        {
          value: 'Excessive sleepiness or sedation',
          key: 'excessive_sedation'
        },
        { value: 'Dizziness', key: 'dizziness' },
        { value: 'Mouth or tongue irritation', key: 'mouth_irritation' },
        {
          value:
            'Liver-related symptoms (yellowing of eyes/skin, dark urine, unusual fatigue)',
          key: 'liver_symptoms'
        },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_sideEffectsOther',
    label: 'Tell us more about your other side effects',
    type: 'info',
    answer: {
      type: 'text',
      name: 'suboxoneFollowup_sideEffectsOther',
      rows: 4
    }
  },
  {
    field: 'suboxoneFollowup_overdoseSinceLastVisit',
    label:
      'Since your last visit, have you had any overdose events or needed naloxone?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes, I received naloxone', key: 'yes_naloxone' }
      ]
    }
  },
  {
    field: 'suboxoneFollowup_treatmentPreference',
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "I'm doing well and I would like to continue what I'm on",
          key: 'continue_current'
        },
        {
          value: 'I like the medication but I would like to increase the dose',
          key: 'increase_dose'
        },
        {
          value: 'I like the medication but I would like to decrease the dose',
          key: 'decrease_dose'
        },
        {
          value:
            'I would like to change the medication if other options are available',
          key: 'change_medication'
        },
        {
          value:
            "I'm not sure, have the provider reach out to discuss further",
          key: 'provider_discuss'
        }
      ]
    }
  }
] as const;

export type SuboxoneQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof suboxoneQuestions>
>;
export default suboxoneQuestions;
